import { useLocation, useNavigate } from 'react-router-dom';
import { AskMarkdown } from './AskMarkdown';
import './recipe-content.css';
import { FilterChipRow } from '../ui/FilterChipRow';
import { ResponseRecipeToolbar } from './ResponseRecipeToolbar';
import { ButtonContent } from '../ui/ButtonContent';
import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import DOMPurify from 'dompurify';
import { IconArrowsShuffle, IconChevronLeft, IconChevronRight, IconPencil, IconTrash, IconX } from '@tabler/icons-react';
import { ContentType } from '../../services/geminiService';
import { captureException, captureEvent } from '../../lib/telemetry/posthog/frontend';
import { useCaseStudies } from '../hooks/useCaseStudies';
import { useRecipes } from '../hooks/useRecipes';
import { useRecipeRelevance } from '../hooks/useRecipeRelevance';
import { rankResponseRecipes } from '../../services/recipeRelevanceService';
import type { Recipe, RecipeCategory } from '../../services/recipeService';
import { RECIPE_CATEGORIES, RECIPE_CATEGORY_LABELS } from '../../services/recipeService';
import { useOrg } from '../../lib/OrgProvider';
import { useSupabase } from '../../lib/SupabaseProvider';
import { Skeleton } from '../ui/Skeleton';
import { useReducedMotion } from '../ui/useReducedMotion';
import { RecipeResultReader, recipeResultPreview, useRecipeResults } from './RecipeResults';
import { RecipeAutoRunControl } from './RecipeAutoRunControl';
import { CopyIcon, CheckIcon, SparkIcon, PlusIcon } from '../icons';
import { Tooltip } from '../Tooltip';
import { SegmentedControl } from '../ui/SegmentedControl';
import { getRecipeDisabledReason } from './recipe-error-presentation';

interface IntervieweeInfo {
  interviewee_name?: string;
  interviewee_job?: string;
  interviewee_company?: string;
}

interface CaseStudyGeneratorProps {
  interviewId: string;
  /** Configure future submissions without running a recipe against a campaign. */
  campaignMode?: boolean;
  responseId?: string;
  interviewStatus: string;
  hasTranscript: boolean;
  /** Saved structured answers can ground recipes without spoken turns. */
  hasStoredAnswers?: boolean;
  intervieweeInfo?: IntervieweeInfo;
  onSaveInterviewee?: (data: IntervieweeInfo) => Promise<void>;
}

/**
 * Conservative allowlist for sanitizing LLM-generated report markup before it
 * is rendered via `dangerouslySetInnerHTML`. Covers the formatting the
 * generation pipeline produces (headings, prose, lists, emphasis, links,
 * quotes, tables) and nothing capable of running script. Anything outside
 * this set is dropped by DOMPurify.
 */
const REPORT_ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr', 'span', 'div',
  'ul', 'ol', 'li',
  'strong', 'em', 'b', 'i', 'u',
  'a', 'blockquote',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
];

/** Attributes allowed on the report markup. `class` keeps the styled blocks
 *  (e.g. `.quick-snapshot`); link attributes are constrained to safe values. */
const REPORT_ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'class'];

/**
 * Strips scripts, inline event handlers, and `javascript:` URIs from
 * LLM-generated HTML, returning markup safe to inject. The prompt-injection
 * path (SA3) makes unsanitized rendering a concrete XSS vector (A21), so all
 * generated content passes through here before it reaches the DOM.
 *
 * @param html Raw HTML produced by the generation pipeline.
 * @returns Sanitized HTML limited to {@link REPORT_ALLOWED_TAGS}.
 */
function sanitizeReportHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: REPORT_ALLOWED_TAGS,
    ALLOWED_ATTR: REPORT_ALLOWED_ATTR,
    // Block all URI schemes except the safe ones (drops `javascript:`).
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}

/** Built-in slugs that map 1:1 to a legacy pipeline content type. */
const LEGACY_BUILTIN_SLUGS = ['case_study', 'expert_article', 'report', 'campaign_report'];

/** One-line descriptions for the built-in recipes (preserve the old copy). */
const BUILTIN_DESCRIPTIONS: Record<string, string> = {
  case_study: 'Turn insights into a compelling customer success narrative.',
  expert_article: 'Craft thought-leadership content from expert perspectives.',
  report: 'Summarize the conversation into a structured internal report.',
  campaign_report: 'Summarize every conversation across the campaign.',
};

/** Pipeline content type a recipe runs as: a legacy built-in slug, or 'custom'. */
function recipeContentType(recipe: Recipe): ContentType {
  if (recipe.isBuiltin && recipe.slug && LEGACY_BUILTIN_SLUGS.includes(recipe.slug)) {
    return recipe.slug as ContentType;
  }
  return 'custom';
}

/**
 * A one-line description for a card. The author's own description wins; failing
 * that, built-in copy, then the prompt's first line as a last resort.
 */
function recipeDescription(recipe: Recipe): string {
  if (recipe.description && recipe.description.trim()) {
    return recipe.description.trim();
  }
  if (recipe.isBuiltin && recipe.slug && BUILTIN_DESCRIPTIONS[recipe.slug]) {
    return BUILTIN_DESCRIPTIONS[recipe.slug];
  }
  return recipe.prompt.split('\n').map((line) => line.trim()).find(Boolean) ?? '';
}

/** Human label for a recipe's curated category, or null when it carries none. */
function recipeCategoryLabel(category: string | null): string | null {
  if (!category) return null;
  return RECIPE_CATEGORY_LABELS[category as RecipeCategory] ?? category;
}

/** Derive a display name for the current user (stamped onto recipes they create). */
function deriveAuthorName(metadata: Record<string, unknown> | undefined, email?: string | null): string | null {
  const pick = (key: string): string => (typeof metadata?.[key] === 'string' ? (metadata[key] as string) : '');
  const full = pick('full_name') || pick('name') || [pick('first_name'), pick('last_name')].filter(Boolean).join(' ').trim();
  return full || email || null;
}

/**
 * The surface a meta pill / ghost button sits on, which fixes its fill so the
 * pill stays a distinct, contrast-clean object there (every value verified in
 * `lib/theme/contrast.ts`, both themes):
 *  - 'raised': the white / raised detail modal (the default).
 *  - 'card':   a normal volt-wash gallery card. Pills take the PAGE colour so
 *              they stay distinct from BOTH the card's resting AND hover state
 *              (the card hovers to `bg-surface-hover`, which is exactly what a
 *              `bg-surface-hover` pill would be, so it would vanish on hover).
 *  - 'mint':   a "ready to review" mint card. Pills take the deeper green.
 */
type PillSurface = 'raised' | 'card' | 'mint';

/** Neutral pill fill per surface (category / tag / author chips). */
const PILL_FILL: Record<PillSurface, string> = {
  raised: 'bg-surface-hover',
  card: 'bg-background',
  mint: 'bg-wash-success-deep',
};

/** The "made by holito" stamp's fill + brand text per surface. On a normal card
 *  a volt WASH is invisible (`--surface` == `--wash-accent` == #F2EEFF in light),
 *  so the card variant uses the page colour with volt text (subtle, not a loud
 *  solid volt); the modal keeps the volt wash (distinct on white), and a mint
 *  card uses the deeper green with flow text. */
const STAMP_STYLE: Record<PillSurface, string> = {
  raised: 'bg-wash-accent text-accent-soft',
  card: 'bg-background text-accent-soft',
  mint: 'bg-wash-success-deep text-success-soft',
};

/** Ghost-button hover fill per surface (distinct from the surface it covers,
 *  including the card's own hover state). */
const GHOST_HOVER: Record<PillSurface, string> = {
  raised: 'hover:bg-surface-hover',
  card: 'hover:bg-background',
  mint: 'hover:bg-wash-success-deep',
};

/** A small meta pill (category / author). Its fill tracks the {@link PillSurface}
 *  it sits on so it never collapses into the surface (or the card's hover state). */
const Chip: React.FC<{ children: React.ReactNode; on?: PillSurface }> = ({ children, on = 'raised' }) => (
  <span className={`px-2.5 py-1 text-sm rounded-lg font-medium text-muted-foreground ${PILL_FILL[on]}`}>
    {children}
  </span>
);

/** A personal-tag pill (the dimmer, owner-applied tag tone). Same surface-aware
 *  fill as {@link Chip}, so a tag never vanishes into a hovered card. */
const TagChip: React.FC<{ children: React.ReactNode; on?: PillSurface }> = ({ children, on = 'raised' }) => (
  <span className={`px-2.5 py-1 text-sm rounded-lg text-subtle-foreground ${PILL_FILL[on]}`}>{children}</span>
);

/**
 * The "made by holito" stamp on every built-in recipe: a "Made by" label plus the
 * holito wordmark, so the brand authorship is unmistakable. The fill tracks the
 * {@link PillSurface} (see {@link STAMP_STYLE}); the wordmark stays theme-aware
 * (`dark:invert`: brand ink on the light fills, white on the dark fills).
 */
const MadeByHolitoBadge: React.FC<{ on?: PillSurface }> = ({ on = 'raised' }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-lg font-medium ${STAMP_STYLE[on]}`}>
    Made by
    <img src="/holito-logo-black.svg" alt="holito" className="h-3 w-auto dark:invert" />
  </span>
);

/**
 * A ghost icon button (hover-reveals colour only, 32x32 minimum hit area). Its
 * hover fill tracks the {@link PillSurface} (see {@link GHOST_HOVER}) so it stays
 * distinct from the surface it covers, including a card's own hover state (a
 * `bg-surface-hover` hover would vanish into a hovered card).
 */
const GhostIconButton: React.FC<{
  label: string;
  tooltip: string;
  onClick: (e: React.MouseEvent) => void;
  on?: PillSurface;
  children: React.ReactNode;
}> = ({ label, tooltip, onClick, on = 'raised', children }) => (
  <Tooltip content={tooltip} position="bottom">
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`min-w-8 min-h-8 p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors duration-150 inline-flex items-center justify-center ${GHOST_HOVER[on]}`}
    >
      {children}
    </button>
  </Tooltip>
);

/**
 * A single recipe in the gallery. The whole card is the inspect affordance:
 * clicking it opens the detail view (full prompt + result). The dark Generate
 * pill runs the pipeline; hover reveals Remix (every recipe) and Edit (own
 * recipes only).
 */
interface RecipeCardProps {
  recipe?: Recipe;
  /** Keep the real card layout while recipe content is unavailable. */
  loading?: boolean;
  isGenerated?: boolean;
  onOpen?: () => void;
  onRemix?: () => void;
  onEdit?: () => void;
  /** Prefer an excerpt of the saved output on completed response cards. */
  content?: string | null;
}

const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  loading = false,
  isGenerated,
  onOpen,
  onRemix,
  onEdit,
  content,
}) => {
  const excerpt = React.useMemo(() => content ? recipeResultPreview(content) : null, [content]);
  const isBuiltin = recipe?.isBuiltin;
  const categoryLabel = recipe ? recipeCategoryLabel(recipe.category) : null;
  const extraTags = Math.max(0, (recipe?.tags.length ?? 0) - 3);
  // A normal card is the volt wash ('card'); a ready card is mint. Pills/buttons
  // pick a fill distinct from that surface (and its hover state).
  const surface: PillSurface = isGenerated ? 'mint' : 'card';

  return (
    <div
      data-testid={recipe ? `recipe-card-${recipe.id}` : undefined}
      aria-busy={loading || undefined}
      className={`group relative flex flex-col w-full h-full p-8 rounded-2xl transition-colors duration-150 text-left min-h-[200px] ${
        isGenerated ? 'bg-wash-success hover:bg-wash-success-hover' : 'bg-surface hover:bg-surface-hover'
      }`}
    >
      {/*
        Stretched open-detail target. A single real <button> covers the card and
        opens the detail view (whole-card click + native keyboard). It sits above
        the static text (so clicks on the prose open the card) but below the real
        action controls, which carry a higher stacking context. The controls are
        siblings, never nested inside this button, so there is no nested
        interactive (a11y) violation.
      */}
      {recipe && <button
        type="button"
        aria-label={`Open ${recipe.name}`}
        onClick={onOpen}
        className="absolute inset-0 z-10 rounded-2xl cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      />}

      {/* Hover/focus-revealed secondary actions (focus-within keeps them out of a
          focus trap while visually hidden). */}
      {recipe && <div className="absolute top-5 right-5 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150">
        <GhostIconButton label="Remix recipe" tooltip="Remix" on={surface} onClick={(e) => { e.stopPropagation(); onRemix?.(); }}>
          <IconArrowsShuffle size={18} strokeWidth={1.6} />
        </GhostIconButton>
        {onEdit && (
          <GhostIconButton label="Edit recipe" tooltip="Edit" on={surface} onClick={(e) => { e.stopPropagation(); onEdit(); }}>
            <IconPencil size={18} strokeWidth={1.6} />
          </GhostIconButton>
        )}
      </div>}

      {/* Title + generated cue. 18px (text-lg) is the on-scale card-heading size. */}
      <div className="flex items-center gap-2 mb-3 pr-16">
        <h4 className={`text-lg font-semibold leading-[1.5] text-foreground${loading ? ' flex-1' : ''}`}>{loading ? <Skeleton /> : recipe?.name}</h4>
        {isGenerated && (
          <span role="img" aria-label="Generated" className="shrink-0 text-success-soft">
            <CheckIcon className="w-4 h-4" />
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground leading-[1.5] line-clamp-2 mb-6">
        {loading ? <Skeleton count={2} /> : excerpt ?? (recipe ? recipeDescription(recipe) : null)}
      </p>

      {/* Meta. Pinned to the card foot (mt-auto) so the row aligns across cards
          of differing description length. */}
      <div className="flex flex-wrap items-center gap-2 mt-auto">
        {loading ? <Skeleton width="8rem" /> : isBuiltin ? (
          <>
            {categoryLabel && <Chip on={surface}>{categoryLabel}</Chip>}
            <MadeByHolitoBadge on={surface} />
          </>
        ) : (
          <>
            {recipe?.tags.slice(0, 3).map((tag) => <TagChip key={tag} on={surface}>#{tag}</TagChip>)}
            {extraTags > 0 && <TagChip on={surface}>+{extraTags}</TagChip>}
            <Chip on={surface}>{`Made by ${recipe?.authorName ?? 'you'}`}</Chip>
          </>
        )}
      </div>
    </div>
  );
};

/** The "+ New recipe" dashed card that opens the editor. */
const NewRecipeCard: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <button
    type="button"
    disabled={!onClick}
    data-testid="recipe-card-new"
    onClick={onClick}
    className="flex flex-col items-center justify-center gap-2 h-full px-8 py-8 rounded-2xl border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-subtle-foreground transition-colors duration-150 min-h-[200px]"
  >
    <PlusIcon className="w-6 h-6" />
    <span className="text-sm font-medium">New recipe</span>
  </button>
);

/**
 * The one field treatment used across these forms (the editor prompt and the
 * guest-detail fields). A filled surface is its own edge, so it carries no
 * resting border; the transparent border only reserves space for the volt
 * focus border, so focusing never shifts the layout. One fill, one radius, so
 * no field reads as a heavier box than its neighbours.
 */
const FIELD_CLASS =
  'w-full px-3 py-2.5 bg-surface border border-transparent rounded-lg text-foreground text-sm focus:outline-none focus:border-volt transition-colors';


/**
 * Pipeline steps mapped to real backend stages.
 * Steps only check off when the backend confirms the next stage has started.
 */
const PIPELINE_STEPS = [
  { label: 'Analyzing transcript', stage: 'strategist' },
  { label: 'Planning content', stage: 'architect' },
  { label: 'Writing draft', stage: 'writing' },
  { label: 'Checking quality', stage: 'evaluating' },
];

/** Ordered stages for comparison. 'revising' maps back to writing step. */
const STAGE_ORDER: string[] = ['strategist', 'architect', 'writing', 'evaluating', 'revising'];

/** Progress bar target for each stage (the bar fills optimistically within the range) */
const STAGE_TARGETS: Record<string, number> = {
  strategist: 25,
  architect: 50,
  writing: 75,
  evaluating: 95,
  revising: 95,
};

/**
 * Determines the visual state of a step based on the current real backend stage.
 */
function getStepState(stepStage: string, currentStage: string | null): 'pending' | 'active' | 'completed' {
  if (!currentStage) return 'pending';

  if (currentStage === 'revising') {
    const stepIdx = STAGE_ORDER.indexOf(stepStage);
    const writingIdx = STAGE_ORDER.indexOf('writing');
    if (stepIdx < writingIdx) return 'completed';
    if (stepStage === 'writing') return 'active';
    return 'pending';
  }

  const currentIdx = STAGE_ORDER.indexOf(currentStage);
  const stepIdx = STAGE_ORDER.indexOf(stepStage);

  if (currentIdx < 0 || stepIdx < 0) return 'pending';
  if (currentIdx > stepIdx) return 'completed';
  if (currentIdx === stepIdx) return 'active';
  return 'pending';
}

/**
 * Progress bar driven by real backend pipeline stages.
 * Steps check off only when the backend confirms stage transitions.
 */
const GenerationProgress: React.FC<{ title: string; stage: string | null; revisionCount: number }> = ({ title, stage, revisionCount }) => {
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const targetRef = useRef(8);
  const stageRef = useRef<string | null>(null);

  useEffect(() => {
    stageRef.current = stage;
    if (!stage) {
      targetRef.current = 8;
      return;
    }
    targetRef.current = STAGE_TARGETS[stage] || targetRef.current;
  }, [stage]);

  useEffect(() => {
    let frame: number;
    let last = performance.now();

    const animate = (now: number) => {
      const deltaMs = Math.min(now - last, 100);
      last = now;

      const current = progressRef.current;
      const target = targetRef.current;
      const remaining = target - current;

      if (remaining > 0.01) {
        const stageDuration = stageRef.current === 'revising' ? 50000 : 25000;
        const alpha = 1 - Math.exp(-deltaMs / stageDuration);
        const next = Math.min(current + remaining * alpha * 8, target);
        progressRef.current = next;
        setProgress(next);
      }

      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="p-8">
      <div className="space-y-6 max-w-md mx-auto">
        <h3 className="text-lg font-bold text-foreground text-center leading-[1.5]">{title}</h3>

        <div className="text-sm text-center">
          {stage === 'revising' ? (
            <p className="text-warning">Improving draft quality (revision {Math.max(1, revisionCount)})</p>
          ) : stage === 'evaluating' && revisionCount > 0 ? (
            <p className="text-volt-soft">Re-checking revised draft (revision {revisionCount})</p>
          ) : (
            <p className="text-muted-foreground">This usually takes a few minutes.</p>
          )}
        </div>

        <div className="space-y-2">
          <div
            className="h-2 bg-surface rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${title} progress`}
          >
            <div
              className="h-full bg-volt rounded-full origin-left will-change-transform"
              style={{ transform: `scaleX(${Math.max(0, Math.min(progress / 100, 1))})` }}
            />
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        <div className="space-y-3">
          {PIPELINE_STEPS.map((step) => {
            const state = getStepState(step.stage, stage);
            return (
              <div
                key={step.stage}
                className={`flex items-center gap-3 text-sm transition-colors duration-300 ${
                  state === 'completed'
                    ? 'text-success-soft'
                    : state === 'active'
                      ? 'text-foreground'
                      : 'text-subtle-foreground'
                }`}
              >
                <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                  {state === 'completed' ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : state === 'active' ? (
                    <div className="w-3 h-3 border-2 border-volt border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <div className="w-2 h-2 bg-surface-hover rounded-full" />
                  )}
                </div>
                <span>{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/** Collects guest details before a first generation when they are missing. */
const IntervieweePromptModal: React.FC<{
  info?: IntervieweeInfo;
  onSave: (data: IntervieweeInfo) => Promise<void>;
  onSkip: () => void;
  onClose: () => void;
  recipeName: string;
}> = ({ info, onSave, onSkip, onClose, recipeName }) => {
  const [form, setForm] = useState({
    interviewee_name: info?.interviewee_name || '',
    interviewee_job: info?.interviewee_job || '',
    interviewee_company: info?.interviewee_company || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSaveAndGenerate = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised border border-transparent light:border-border rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-start gap-3 mb-5">
          <div className="p-2 bg-wash-accent rounded-lg">
            <svg className="w-5 h-5 text-volt-soft" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground leading-[1.5]">Add guest details</h3>
            <p className="text-sm text-muted-foreground mt-1">
              A name is required for {recipeName}. Job title and company are optional.
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <div>
            <label className="text-sm text-muted-foreground font-medium">Full name <span className="text-danger-soft">*</span></label>
            <input
              type="text"
              value={form.interviewee_name}
              onChange={e => setForm(f => ({ ...f, interviewee_name: e.target.value }))}
              className={`${FIELD_CLASS} mt-1`}
              placeholder="e.g. Sarah Johnson"
              autoFocus
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground font-medium">Job title</label>
            <input
              type="text"
              value={form.interviewee_job}
              onChange={e => setForm(f => ({ ...f, interviewee_job: e.target.value }))}
              className={`${FIELD_CLASS} mt-1`}
              placeholder="e.g. Head of Marketing"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground font-medium">Company</label>
            <input
              type="text"
              value={form.interviewee_company}
              onChange={e => setForm(f => ({ ...f, interviewee_company: e.target.value }))}
              className={`${FIELD_CLASS} mt-1`}
              placeholder="e.g. Acme Corp"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button aria-busy={saving}
            onClick={handleSaveAndGenerate}
            disabled={saving || !form.interviewee_name.trim()}
            className="w-full py-3 button-accent font-bold rounded-lg transition-colors disabled:opacity-50"
          >
            <ButtonContent loading={saving}>Save and run</ButtonContent>
          </button>
          <button
            onClick={onSkip}
            className="w-full py-2.5 text-muted-foreground text-sm font-medium hover:text-foreground transition-colors"
          >
            Skip, run without details
          </button>
        </div>
      </div>
    </div>
  );
};

/** A hover-revealed carousel nudge control (one per scroll direction). */
const CarouselButton: React.FC<{ side: 'left' | 'right'; onClick: () => void }> = ({ side, onClick }) => (
  <button
    type="button"
    aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
    onClick={onClick}
    className={`absolute top-1/2 -translate-y-1/2 ${side === 'left' ? 'left-1' : 'right-1'} z-10 min-w-9 min-h-9 rounded-full bg-surface-raised text-foreground inline-flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 focus-visible:opacity-100 transition-opacity duration-150 hover:bg-surface-hover`}
  >
    {side === 'left'
      ? <IconChevronLeft size={18} strokeWidth={1.8} aria-hidden="true" />
      : <IconChevronRight size={18} strokeWidth={1.8} aria-hidden="true" />}
  </button>
);

/**
 * A horizontally scrolling shelf of recipe cards. Each child becomes a
 * fixed-width slide and the track scrolls sideways; chevron nudges fade in on
 * hover and only while there is more to reveal in that direction.
 */
const RecipeCarousel: React.FC<{ children: React.ReactNode; order?: string }> = ({ children, order }) => {
  const reducedMotion = useReducedMotion();
  const trackRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const slidePositions = useRef(new Map<HTMLElement, number>());
  // Filtering already animates slide widths. Only translate cards when their order changes.
  React.useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || !order) return;
    const next = new Map<HTMLElement, number>();
    for (const node of track.children) {
      if (!(node instanceof HTMLElement)) continue;
      const left = node.offsetLeft;
      next.set(node, left);
      const delta = (slidePositions.current.get(node) ?? left) - left;
      if (delta && !reducedMotion) {
        node.animate?.([{ transform: `translateX(${delta}px)` }, { transform: 'translateX(0)' }],
          { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      }
    }
    slidePositions.current = next;
  }, [order, reducedMotion]);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    measure();
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, children]);

  const nudge = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 280), behavior: reducedMotion ? 'instant' : 'smooth' });
  };

  return (
    <div className="group/carousel relative">
      {/*
        Every child is a RecipeSlide that owns its own fixed width, so a slide
        can collapse to zero when its recipe is filtered out and the rest glide
        into the freed space. `items-stretch` (the flex default, set explicitly)
        equalizes slide heights; each slide fills that height and its card fills
        the slide (h-full), so every card matches the tallest in the row.
      */}
      <div
        ref={trackRef}
        onScroll={measure}
        onTransitionEnd={event => {
          if (event.propertyName === 'width') measure();
        }}
        data-testid="recipe-carousel"
        className="flex items-stretch justify-start overflow-x-auto scrollbar-hide snap-x py-2"
      >
        {children}
      </div>
      {/* Edge fades dissolve the row into the page, hinting at more cards just
          out of view. They track the same scroll state as the nudges and never
          intercept clicks (pointer-events-none). */}
      {canLeft && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background to-transparent"
        />
      )}
      {canRight && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent"
        />
      )}
      {canLeft && <CarouselButton side="left" onClick={() => nudge(-1)} />}
      {canRight && <CarouselButton side="right" onClick={() => nudge(1)} />}
    </div>
  );
};

/**
 * One carousel slide. It owns its width so it can collapse to zero when its
 * recipe is filtered out: the remaining cards then glide left into the freed
 * space (width, margin, and opacity ease on the brand curve) rather than
 * snapping. A hidden slide is inert and unannounced. The inner fixed-width box
 * keeps the card from reflowing while the outer width animates; the card fills
 * the slide height via h-full so the row stays even.
 */
const SLIDE_WIDTH = 'min(340px, calc(100vw - 3rem))';
const SLIDE_GAP = 16;

const RecipeSlide: React.FC<{ hidden?: boolean; children: React.ReactNode }> = ({ hidden = false, children }) => (
  <div
    aria-hidden={hidden || undefined}
    inert={hidden}
    // Width/margin are inline so the fixed slide size never depends on an
    // arbitrary Tailwind class being generated; the transition eases the
    // collapse to zero when the slide is filtered out.
    style={{ width: hidden ? 0 : SLIDE_WIDTH, marginRight: hidden ? 0 : SLIDE_GAP }}
    className={`shrink-0 snap-start overflow-hidden transition-all duration-300 motion-reduce:transition-none ${
      hidden ? 'opacity-0 pointer-events-none' : 'opacity-100'
    }`}
  >
    <div style={{ width: SLIDE_WIDTH }} className="h-full">{children}</div>
  </div>
);

/**
 * A labelled section of the gallery, laid out as a carousel. Renders nothing when
 * it has no cards, OR when the active filter (`isHidden`) has hidden every card it
 * would show: an empty heading over a zero-width row is just dead white space, so
 * the whole band collapses out of the stack instead.
 */
const RecipeBand: React.FC<{
  label: string;
  recipes: Recipe[];
  renderCard: (recipe: Recipe) => React.ReactNode;
  isHidden?: (r: Recipe) => boolean;
  trailing?: React.ReactNode;
  loading?: boolean;
}> = ({ label, recipes, renderCard, isHidden, trailing, loading = false }) => {
  const visibleCount = isHidden ? recipes.reduce((n, r) => (isHidden(r) ? n : n + 1), 0) : recipes.length;
  if (visibleCount === 0 && !trailing && !loading) return null;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground">{label}</h3>
      <RecipeCarousel>
        {loading && [0, 1].map(index => <RecipeSlide key={`loading-${index}`}><RecipeCard loading /></RecipeSlide>)}
        {recipes.map(renderCard)}
        {trailing}
      </RecipeCarousel>
    </div>
  );
};

/** The gallery presentation also serves route loading without starting data hooks. */
export function RecipeGalleryView({ loading = false, children }: { loading?: boolean; children?: React.ReactNode }) {
  return <div className="space-y-10" role={loading ? 'status' : undefined} aria-label={loading ? 'Loading recipes' : undefined} aria-busy={loading || undefined}>
    {children ?? <>
      <RecipeBand label="Made by holito" recipes={[]} renderCard={() => null} loading={loading} />
      <div className="space-y-3"><RecipeCarousel><div className="shrink-0 snap-start mr-4"><NewRecipeCard /></div></RecipeCarousel></div>
    </>}
  </div>;
}

/** The inline `<style>` for the generated content reader. */
const READER_STYLE = `
  .case-study-content { color: #e5e5e5; line-height: 1.7; }
  .case-study-content h1 { color: #ffffff; font-size: 1.75rem; font-weight: 700; margin: 0 0 1rem 0; }
  .case-study-content h2 { color: #a78bfa; font-size: 1.25rem; font-weight: 600; margin: 1.5rem 0 0.75rem 0; }
  .case-study-content h3 { color: #c4b5fd; font-size: 1.1rem; font-weight: 600; margin: 1.25rem 0 0.5rem 0; }
  .case-study-content p { color: #d4d4d4; margin: 0 0 1rem 0; }
  .case-study-content blockquote { padding: 0.75rem 1rem; margin: 1rem 0; background: rgba(139, 92, 246, 0.1); border-radius: 0.5rem; font-style: italic; color: #a3a3a3; }
  .case-study-content ul { list-style: disc; padding-left: 1.5rem; margin: 1rem 0; }
  .case-study-content li { color: #d4d4d4; margin: 0.5rem 0; }
  .case-study-content strong { color: #ffffff; font-weight: 600; }
  .case-study-content .quick-snapshot { background: rgba(139, 92, 246, 0.08); border-radius: 0.75rem; padding: 1.25rem 1.5rem; margin: 1.25rem 0 1.5rem 0; }
  .case-study-content .quick-snapshot h2 { margin-top: 0; font-size: 1.1rem; }
  .case-study-content .quick-snapshot p { margin: 0.5rem 0; font-size: 0.95rem; }
  [data-theme="light"] .case-study-content { color: #171717; }
  [data-theme="light"] .case-study-content h1 { color: #0a0a0a; }
  [data-theme="light"] .case-study-content h2 { color: #6d28d9; }
  [data-theme="light"] .case-study-content h3 { color: #7c3aed; }
  [data-theme="light"] .case-study-content p { color: #262626; }
  [data-theme="light"] .case-study-content blockquote { color: #525252; background: rgba(139, 92, 246, 0.06); }
  [data-theme="light"] .case-study-content li { color: #262626; }
  [data-theme="light"] .case-study-content strong { color: #0a0a0a; }
  [data-theme="light"] .case-study-content .quick-snapshot { background: rgba(139, 92, 246, 0.05); }
`;

/** The single filled volt CTA in the detail view: the one place a recipe runs. */
const PrimaryCta: React.FC<{
  label: string;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}> = ({ label, disabled = false, disabledReason, onClick }) => {
  const reasonId = useId();
  const showReason = disabled && Boolean(disabledReason);

  return (
    <div className="flex shrink-0 flex-col items-end gap-2">
      <button
        type="button"
        disabled={disabled}
        aria-describedby={showReason ? reasonId : undefined}
        onClick={onClick}
        className="button-accent rounded-lg px-6 py-3 text-sm font-bold leading-none flex items-center gap-2 transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <SparkIcon className="w-4 h-4" aria-hidden="true" />
        {label}
      </button>
      {showReason && (
        <p id={reasonId} className="max-w-56 text-right text-xs text-muted-foreground">
          {disabledReason}
        </p>
      )}
    </div>
  );
};

/** Above this many characters the prompt card collapses behind a Show more toggle. */
const PROMPT_COLLAPSE_CHARS = 320;

/** A read-only prompt card. Long prompts collapse behind a Show more toggle. */
const PromptCard: React.FC<{ prompt: string }> = ({ prompt }) => {
  const collapsible = prompt.length > PROMPT_COLLAPSE_CHARS;
  const [expanded, setExpanded] = useState(false);
  const clipped = collapsible && !expanded;

  return (
    <div className="rounded-2xl bg-surface p-6">
      <h3 className="text-sm font-semibold text-subtle-foreground mb-3">Prompt</h3>
      {/* The reveal eases its height open on the brand curve, never snapping to
          full. overflow-hidden clips the body while it grows. */}
      <div
        className={`text-sm leading-[1.6] text-foreground overflow-hidden transition-[max-height] duration-500 motion-reduce:transition-none ${
          clipped ? 'max-h-40' : 'max-h-[1200px]'
        }`}
      >
        <AskMarkdown source={prompt} />
      </div>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-soft hover:text-foreground transition-colors duration-150"
        >
          <AnimatedLabel text={expanded ? 'Show less' : 'Show more'} reserveText="Show more" />
          <PlusIcon
            className={`w-4 h-4 transition-transform duration-200 motion-reduce:transition-none ${expanded ? 'rotate-45' : ''}`}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
};

/**
 * The recipe detail view: a soft volt-wash hero with a centered name-chip, the
 * large slash-prefixed title, a one-line description, a meta row, an action row
 * (remix, edit, and the single filled Try-recipe CTA), then prompt transparency
 * plus the generated result. The full prompt is always readable (built-ins
 * included); when a result exists a Result | Prompt toggle switches between the
 * rendered content and the prompt.
 */
interface RecipeDetailProps {
  /** Campaign that receives future submissions. */
  sessionId: string;
  campaignMode?: boolean;
  recipe: Recipe;
  content: string | null;
  error: string | null;
  copied: boolean;
  copyError: string | null;
  canRun: boolean;
  disabledReason: string;
  onCopy: () => void;
  onGenerate: () => void;
  onRemix: () => void;
  /** Present only for the user's own (non-builtin) recipe; opens the editor. */
  onEdit?: () => void;
  /** Present only for the user's own recipe; archives it after an inline confirm. */
  onDelete?: () => void;
}

const RecipeDetail: React.FC<RecipeDetailProps> = ({
  sessionId,
  campaignMode = false,
  recipe,
  content,
  error,
  copied,
  copyError,
  canRun,
  disabledReason,
  onCopy,
  onGenerate,
  onRemix,
  onEdit,
  onDelete,
}) => {
  const isGenerated = !!content;
  const [tab, setTab] = useState<'result' | 'prompt'>('result');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const showResult = isGenerated && tab === 'result';
  const isBuiltin = recipe.isBuiltin;
  const categoryLabel = recipeCategoryLabel(recipe.category);
  const wordCount = content
    ? content.replace(/<[^>]*>/g, '').split(/\s+/).filter((w) => w.length > 0).length
    : 0;

  return (
    <div className="space-y-6">
      {/* The heading leads the block: a left-aligned slash-title, then a one-line
          description. No centred hero band, no label stacked above the heading. */}
      <div>
        <h2 className="text-[22px] font-semibold leading-[1.4] text-foreground">
          <span className="text-accent-soft" aria-hidden="true">/ </span>
          {recipe.name}
        </h2>
        <p className="mt-2 text-lg text-muted-foreground leading-[1.5]">{recipeDescription(recipe)}</p>
      </div>

      {/* Meta row. Scope ("Conversation") and output ("Doc") are intentionally
          omitted: in this gallery every recipe is conversation-scope + Doc, so
          those chips were constant noise that told the user nothing. */}
      <div className="flex flex-wrap items-center gap-2">
        {isBuiltin
          ? <MadeByHolitoBadge />
          : <Chip>{`Made by ${recipe.authorName ?? 'you'}`}</Chip>}
        {isBuiltin
          ? categoryLabel && <Chip>{categoryLabel}</Chip>
          : recipe.tags.slice(0, 3).map((tag) => <TagChip key={tag}>#{tag}</TagChip>)}
      </div>

      {/* Action row: low-emphasis remix/edit/delete, one filled Try-recipe CTA. */}
      <div className="flex items-center gap-1">
        <GhostIconButton label="Remix recipe" tooltip="Remix" onClick={onRemix}>
          <IconArrowsShuffle size={18} strokeWidth={1.6} />
        </GhostIconButton>
        {onEdit && (
          <GhostIconButton label="Edit recipe" tooltip="Edit" onClick={onEdit}>
            <IconPencil size={18} strokeWidth={1.6} />
          </GhostIconButton>
        )}
        {onDelete && (
          confirmingDelete ? (
            <span className="inline-flex items-center gap-2 pl-1 animate-fade-content">
              <span className="text-sm text-muted-foreground">Delete recipe?</span>
              <button
                type="button"
                onClick={() => { setConfirmingDelete(false); onDelete(); }}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-danger text-danger-foreground hover:opacity-90 transition-opacity duration-150"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors duration-150"
              >
                Cancel
              </button>
            </span>
          ) : (
            <GhostIconButton label="Delete recipe" tooltip="Delete" onClick={() => setConfirmingDelete(true)}>
              <IconTrash size={18} strokeWidth={1.6} />
            </GhostIconButton>
          )
        )}
        <div className="flex-1" />
        {!campaignMode && <PrimaryCta
          label={isGenerated ? 'Regenerate' : 'Generate'}
          disabled={!canRun}
          disabledReason={disabledReason}
          onClick={onGenerate}
        />}
      </div>

      {error && <p role="alert" className="text-sm text-danger-soft">{error}</p>}

      <div className="border-t border-border-soft" />

      <RecipeAutoRunControl key={`${sessionId}:${recipe.id}`} sessionId={sessionId} recipeId={recipe.id} eligible={recipe.scope === 'conversation' && recipe.outputKind === 'html'} />

      {/* Result | Prompt toggle (only once a result exists) */}
      {isGenerated && (
        <SegmentedControl<'result' | 'prompt'>
          options={[
            { value: 'result', label: 'Result' },
            { value: 'prompt', label: 'Prompt' },
          ]}
          value={tab}
          onChange={setTab}
          ariaLabel="View result or prompt"
        />
      )}

      {/* Cross-fade the toggled content: the keyed wrapper remounts so the new
          view eases in on opacity, never snapping between Result and Prompt. */}
      <div key={showResult ? 'result' : 'prompt'} className="animate-fade-content">
      {showResult ? (
        <div>
          {copyError && <p role="alert" className="mb-3 text-sm text-danger-soft">{copyError}</p>}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-subtle-foreground">{wordCount.toLocaleString()} words</span>
            <button
              onClick={onCopy}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${
                copied ? 'bg-success text-success-foreground' : 'bg-surface hover:bg-surface-hover text-foreground'
              }`}
            >
              {copied ? (<><CheckIcon className="w-4 h-4" />Copied</>) : (<><CopyIcon className="w-4 h-4" />Copy</>)}
            </button>
          </div>
          <style>{READER_STYLE}</style>
          {/* The result sits on its own neutral surface, the same as the prompt
              card, so it reads as a distinct section of content. */}
          <div className="rounded-2xl bg-surface p-6">
            <div
              className="case-study-content recipe-content overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: sanitizeReportHtml(content ?? '') }}
            />
          </div>
        </div>
      ) : (
        <PromptCard prompt={recipe.prompt} />
      )}
      </div>
    </div>
  );
};

/**
 * The fixed-size modal that hosts a selected recipe: a centered, scrollable
 * dialog over a dimmed gallery (the detail is an overlay, never a full-page
 * navigation). Closes on the X, a backdrop click, or Escape.
 */
const RecipeDetailModal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative bg-surface-raised border border-transparent light:border-border rounded-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden"
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 min-w-8 min-h-8 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors duration-150 inline-flex items-center justify-center"
        >
          <IconX size={18} strokeWidth={1.6} aria-hidden="true" />
        </button>
        <div className="min-h-0 overflow-y-auto scrollbar-dark p-6">{children}</div>
      </div>
    </div>
  );
};

/**
 * Recipe gallery: lists the conversation-scope recipes a user can run against a
 * response (built-ins made by holito, teammates' recipes, and the user's own),
 * runs them through the generation pipeline keyed by recipe id, and renders the
 * generated content. Campaign-scope and structured recipes are authored/run
 * elsewhere (Phase 3) and never appear here.
 */
export const CaseStudyGenerator: React.FC<CaseStudyGeneratorProps> = ({
  interviewId,
  campaignMode = false,
  responseId,
  interviewStatus,
  hasTranscript,
  hasStoredAnswers = false,
  intervieweeInfo,
  onSaveInterviewee,
}) => {
  const { activeOrg } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSupabase();
  const authorName = deriveAuthorName(user?.user_metadata as Record<string, unknown> | undefined, user?.email);

  const {
    builtins,
    team,
    mine,
    myTags,
    loading: recipesLoading,
    archive,
  } = useRecipes({ organizationId: activeOrg?.id ?? null, authorName });

  const {
    existingContent: manualContent,
    inProgressByRecipe,
    loading: contentLoading,
    generatingRecipeId,
    currentStage,
    revisionCount,
    errorByRecipe,
    transcriptQuality,
    selectedRecipeId,
    setSelectedRecipeId,
    handleGenerate,
  } = useCaseStudies({ interviewId, responseId, enabled: !campaignMode });

  const results = useRecipeResults(interviewId, responseId, !!responseId && !campaignMode, manualContent);
  const savedByRecipe = new Map(results.cards.map(card => [card.recipeId, card]));
  const existingContent = results.contentByRecipe;
  const [readOnlyResult, setReadOnlyResult] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();

  // Editor + interviewee modal state (UI-only).
  const [pendingRunRecipe, setPendingRunRecipe] = useState<Recipe | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  // The single page-wide filter selection. null is the unfiltered ("All") state;
  // a value is a namespaced `cat:<category>` or `tag:<tag>`, so a category and a
  // personal tag can never collide on the same selection.
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  // Per-conversation gallery: only conversation-scope html recipes can run here.
  const filter = (r: Recipe) => r.scope === 'conversation' && r.outputKind === 'html';
  const builtinCards = builtins.filter(filter);
  const teamCards = team.filter(filter);
  const mineCards = mine.filter(filter);
  const allRecipes = [...builtins, ...team, ...mine];

  const runnable = [...builtinCards, ...teamCards, ...mineCards];
  const catalog = new Map(runnable.map(recipe => [recipe.id, recipe]));
  const relevance = useRecipeRelevance(interviewId, campaignMode ? undefined : responseId, activeOrg?.id, runnable.map(recipe => recipe.id));
  const suggestions = relevance ? rankResponseRecipes(runnable, relevance) : runnable;
  // Live results arrive newest first. Keep saved outputs readable even when their recipe is archived.
  const shelfIds = [...new Set([
    ...savedByRecipe.keys(),
    ...runnable.filter(recipe => existingContent[recipe.id]?.trim()).map(recipe => recipe.id),
    ...suggestions.map(recipe => recipe.id),
  ])];
  // The combined filter bar merges two discovery dimensions: built-in categories
  // (surfaced only when built-ins span 2+ categories) and the user's personal
  // tags (only with 2+ tags). Values are namespaced so the two never collide.
  const builtinCategories = RECIPE_CATEGORIES.filter((c) => builtinCards.some((r) => r.category === c));
  const showCategoryFilter = builtinCategories.length >= 2;
  const showTagFilter = myTags.length >= 2;
  const filterOptions = [
    ...(showCategoryFilter ? builtinCategories.map((c) => ({ value: `cat:${c}`, label: recipeCategoryLabel(c) ?? c })) : []),
    ...(showTagFilter ? myTags.map((t) => ({ value: `tag:${t}`, label: `#${t}` })) : []),
  ];

  // The active filter applies page-wide. Filtering is animated by collapsing the
  // non-matching slides (see RecipeSlide), so every card stays mounted and this
  // predicate only marks which ones are hidden. A category selection collapses
  // recipes without that category (uncategorised team/own recipes included); a
  // tag selection collapses recipes lacking that tag.
  const isHidden = (r: Recipe): boolean => {
    if (term && ![r.name, recipeDescription(r), recipeCategoryLabel(r.category), ...r.tags]
      .some(value => value?.toLowerCase().includes(term))) return true;
    if (!selectedFilter) return false;
    if (selectedFilter.startsWith('cat:')) return r.category !== selectedFilter.slice(4);
    if (selectedFilter.startsWith('tag:')) return !r.tags.includes(selectedFilter.slice(4));
    return false;
  };

  const canGenerate = !campaignMode && interviewStatus === 'completed' && (hasTranscript || hasStoredAnswers);
  const qualityMet = hasStoredAnswers || Boolean(transcriptQuality?.meetsThreshold);
  const hasInProgress = !!generatingRecipeId || Object.values(inProgressByRecipe).some(Boolean);
  const canRun = canGenerate && qualityMet && !hasInProgress;
  const disabledReason = getRecipeDisabledReason({
    interviewStatus,
    hasTranscript: hasTranscript || hasStoredAnswers,
    userTurnCount: hasStoredAnswers ? null : transcriptQuality?.userTurnCount ?? null,
    qualityMet,
    hasInProgress,
  });

  const runRecipe = (recipe: Recipe) => {
    // Running a recipe that already has content is a Regenerate: force it so the
    // server overwrites the stored row instead of handing the old one straight back.
    const force = !!existingContent[recipe.id];
    void handleGenerate({ id: recipe.id, contentType: recipeContentType(recipe) }, { force });
  };

  const requestRun = (recipe: Recipe) => {
    if (!intervieweeInfo?.interviewee_name) {
      setPendingRunRecipe(recipe);
    } else {
      runRecipe(recipe);
    }
  };

  /** Keep the response or campaign gallery address when opening the shared authoring page. */
  const editorPath = (id: string, remix?: string) => {
    const returnParams = new URLSearchParams(location.search);
    returnParams.set('tab', campaignMode ? 'automate' : 'recipes');
    const params = new URLSearchParams({ returnTo: `${location.pathname}?${returnParams}` });
    if (remix) params.set('remix', remix);
    return `/admin/recipes/${encodeURIComponent(id)}?${params}`;
  };
  const openEdit = (recipe: Recipe) => { void navigate(editorPath(recipe.id)); };
  const openRemix = (recipe: Recipe) => { void navigate(editorPath('new', recipe.id)); };
  const openNew = () => { void navigate(editorPath('new')); };

  /** Archive (remove) the user's own recipe, then return to the gallery. */
  const handleDeleteRecipe = async (recipe: Recipe) => {
    try {
      await archive(recipe.id);
      setSelectedRecipeId(null);
    } catch {
      // archive() already records the error + captures it; the recipe stays.
    }
  };

  const selectedRecipe = selectedRecipeId
    ? allRecipes.find((r) => r.id === selectedRecipeId) ?? null
    : null;
  const selectedContent = selectedRecipeId ? existingContent[selectedRecipeId] : null;

  const handleCopy = async () => {
    if (!selectedContent) return;
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = selectedContent;

      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([selectedContent], { type: 'text/html' }),
              'text/plain': new Blob([tempDiv.innerText], { type: 'text/plain' }),
            }),
          ]);
          setCopied(true);
          setCopyError(null);
          captureEvent('content_copied', { interview_id: interviewId, recipe_id: selectedRecipeId, format: 'rich' });
          setTimeout(() => setCopied(false), 2000);
          return;
        } catch {
          // Fall through to plain text.
        }
      }

      await navigator.clipboard.writeText(tempDiv.innerText);
      setCopied(true);
      setCopyError(null);
      captureEvent('content_copied', { interview_id: interviewId, recipe_id: selectedRecipeId, format: 'plain' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopyError('Failed to copy to clipboard');
      captureException(
        err instanceof Error ? err : new Error('Clipboard copy failed'),
        'CaseStudyGenerator.handleCopy',
        { interview_id: interviewId, recipe_id: selectedRecipeId },
      );
    }
  };

  const intervieweeModal = pendingRunRecipe && (
    <IntervieweePromptModal
      info={intervieweeInfo}
      recipeName={pendingRunRecipe.name}
      onSave={async (data) => {
        if (onSaveInterviewee) await onSaveInterviewee(data);
        const recipe = pendingRunRecipe;
        setPendingRunRecipe(null);
        runRecipe(recipe);
      }}
      onSkip={() => {
        const recipe = pendingRunRecipe;
        setPendingRunRecipe(null);
        runRecipe(recipe);
      }}
      onClose={() => setPendingRunRecipe(null)}
    />
  );

  const loading = contentLoading || recipesLoading;

  const activeError = selectedRecipeId ? errorByRecipe[selectedRecipeId] : null;

  /** Editing is shared within the organization; only the creator can delete. */
  const ownsRecipe = (recipe: Recipe) => !recipe.isBuiltin && recipe.organizationId === activeOrg?.id && recipe.createdBy === user?.id;
  const canEditRecipe = (recipe: Recipe) => !recipe.isBuiltin && recipe.organizationId === activeOrg?.id && (recipe.visibility === 'org' || ownsRecipe(recipe));
  const ownsSelected = selectedRecipe ? ownsRecipe(selectedRecipe) : false;
  const detailModal = selectedRecipe && (
    <RecipeDetailModal title={selectedRecipe.name} onClose={() => setSelectedRecipeId(null)}>
      {generatingRecipeId === selectedRecipe.id ? (
        <GenerationProgress title={selectedRecipe.name} stage={currentStage} revisionCount={revisionCount} />
      ) : (
        <RecipeDetail
          sessionId={interviewId}
          campaignMode={campaignMode}
          recipe={selectedRecipe}
          content={selectedContent}
          error={activeError}
          copied={copied}
          copyError={copyError}
          canRun={canRun}
          disabledReason={disabledReason}
          onCopy={handleCopy}
          onGenerate={() => requestRun(selectedRecipe)}
          onRemix={() => openRemix(selectedRecipe)}
          onEdit={canEditRecipe(selectedRecipe) ? () => openEdit(selectedRecipe) : undefined}
          onDelete={ownsSelected ? () => { void handleDeleteRecipe(selectedRecipe); } : undefined}
        />
      )}
    </RecipeDetailModal>
  );

  // Gallery. Every card renders inside a RecipeSlide; `isHidden` collapses the
  // slide (animated) when the active filter excludes that recipe.
  const renderCard = (recipe: Recipe) => (
    <RecipeSlide key={recipe.id} hidden={isHidden(recipe)}>
      <RecipeCard
        recipe={recipe}
        isGenerated={!!existingContent[recipe.id]?.trim()}
        content={existingContent[recipe.id]}
        onOpen={() => setSelectedRecipeId(recipe.id)}
        onRemix={() => openRemix(recipe)}
        onEdit={canEditRecipe(recipe) ? () => openEdit(recipe) : undefined}
      />
    </RecipeSlide>
  );

  // The New card is a content-width slide (not the fixed recipe-card width), so
  // it stays thin and the first recipe packs right up against it, no dead space.
  const newCard = (
    <div key="new" className="shrink-0 snap-start mr-4">
      <NewRecipeCard onClick={openNew} />
    </div>
  );

  const hasMatches = shelfIds.some(id => {
    const recipe = catalog.get(id);
    return recipe ? !isHidden(recipe) : !selectedFilter && savedByRecipe.get(id)?.title.toLowerCase().includes(term);
  });

  return (
    <>
      {campaignMode ? <RecipeGalleryView loading={loading}>
        {filterOptions.length > 0 && <FilterChipRow
          options={filterOptions} selected={selectedFilter} onSelect={setSelectedFilter} ariaLabel="Recipe filters" />}

        <RecipeBand label="Made by holito" recipes={builtinCards} renderCard={renderCard} isHidden={isHidden}
          loading={recipesLoading && allRecipes.length === 0} />

        <RecipeBand label="Your team" recipes={teamCards} renderCard={renderCard} isHidden={isHidden} />

        <div className="space-y-3">
          {/* The heading shows once the user owns any recipe (even if the active
              filter currently hides them all). */}
          {mineCards.length > 0 && (
            <h3 className="text-sm font-medium text-muted-foreground">My recipes</h3>
          )}
          {/* The New card always leads this carousel, so it is the first slide
              (visible without scrolling) regardless of the active filter and even
              when there are no recipes yet. */}
          <RecipeCarousel>
            {newCard}
            {mineCards.map(renderCard)}
          </RecipeCarousel>
        </div>
      </RecipeGalleryView> : <section aria-label="Recipes" className="min-w-0 space-y-4">
        <h2 className="text-base font-semibold text-foreground">Recipes</h2>
        <ResponseRecipeToolbar options={filterOptions} selected={selectedFilter} onSelect={setSelectedFilter} search={search} onSearch={setSearch} />
        <div role={loading ? 'status' : undefined} aria-label={loading ? 'Loading recipes' : undefined} aria-busy={loading || undefined}>
          <RecipeCarousel order={shelfIds.join(',')}>
            {recipesLoading && !shelfIds.length && <RecipeSlide><RecipeCard loading /></RecipeSlide>}
            {shelfIds.map(id => {
              const recipe = catalog.get(id);
              if (recipe) return renderCard(recipe);
              const result = savedByRecipe.get(id)!;
              return <RecipeSlide key={id} hidden={!!selectedFilter || !result.title.toLowerCase().includes(term)}>
                <button type="button" aria-label={`Open ${result.title}`} onClick={() => setReadOnlyResult(id)}
                  className="flex h-full min-h-[200px] w-full cursor-pointer flex-col items-start gap-3 rounded-2xl bg-wash-success p-8 text-left transition-colors hover:bg-wash-success-hover focus-visible:outline-ring">
                  <span className="text-lg font-semibold text-foreground">{result.title}</span>
                  <span className="line-clamp-3 text-sm text-muted-foreground">{recipeResultPreview(result.content ?? '')}</span>
                </button>
              </RecipeSlide>;
            })}
            {newCard}
          </RecipeCarousel>
          {!loading && !hasMatches && (term || selectedFilter) && <p className="text-sm text-muted-foreground" role="status">No matching recipes.</p>}
        </div>
        {results.cards.length >= results.limit && <button type="button" onClick={results.showMore} className="text-sm text-muted-foreground underline">Show more results</button>}
        {results.error && <p role="alert" className="flex gap-3 text-sm text-danger-soft">Could not load recipe results
          <button type="button" onClick={results.retry} className="underline">Retry</button>
        </p>}
        <RecipeResultReader sessionId={interviewId} selected={readOnlyResult ? savedByRecipe.get(readOnlyResult) : undefined}
          onClose={() => setReadOnlyResult(null)} />
      </section>}
      {/* The detail modal sits above the gallery; the editor / interviewee modals
          stack above the detail modal in turn. */}
      {detailModal}
      {intervieweeModal}
    </>
  );
};

export default CaseStudyGenerator;
